import { useEffect, useMemo, useState } from 'react';
import { Stack, IconButton, Typography, Popover, Box, Checkbox, Button } from '@mui/material';
import LaunchIcon from '@mui/icons-material/Launch';
import CloseIcon from '@mui/icons-material/Close';
import CollectionsOutlinedIcon from '@mui/icons-material/CollectionsOutlined';
import { RegistrationTab } from './RegistrationTab';
import { useTBContext } from '../Contexts/useTBStates';
import type { RegistrationFieldItem } from '@moment-golf/types';
import { allRegistrationFields } from '../../shared/config/RegistrationFields';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ClearIcon from '@mui/icons-material/Clear';
import createAPI from '../../shared/Settings/appSettings';
import { useAuth } from 'react-oidc-context';
import { ImageLibrary } from '../../shared/Components/ImageLibrary';



interface RegistrationFormPopoutProps {
    compHeights: any;
    appDims: any;
    saveEvent: () => void;
}


export const RegistrationFormPopout = ({ compHeights, appDims, saveEvent}: RegistrationFormPopoutProps) => {
    
    const api = createAPI();
    const auth = useAuth();

    const { factor, appTheme, viewEvent, setViewEvent} = useTBContext();
    
    const [open, setOpen] = useState(false);
    const [imageLibraryOpen, setImageLibraryOpen] = useState(false);
    const [selectedFields, setSelectedFields] = useState<any[]>(JSON.parse(JSON.stringify(viewEvent?.registrationForm || [])));

    const assignmentMode = viewEvent?.flighting?.props?.assignmentMode ?? 'auto';
    const availableFields = useMemo(() => {
        const hideDivisionFlight = assignmentMode === 'auto' && viewEvent?.flighting;
        const pool = hideDivisionFlight
            ? allRegistrationFields.filter((f: RegistrationFieldItem) => f.name !== 'division' && f.name !== 'flight')
            : allRegistrationFields;
        return pool.filter((f: RegistrationFieldItem) => !selectedFields.some((s: any) => s.name === f.name));
    }, [assignmentMode, viewEvent?.flighting, selectedFields]);

    useEffect(() => {
        setViewEvent((prev) => (prev ? { ...prev, registrationForm: selectedFields } : null));
        console.log('Updated viewEvent Registration Form');
        console.log(selectedFields);
    }, [selectedFields, setViewEvent]);

    if (!viewEvent) return null;
    const event = viewEvent;

    return (
        <Stack direction={'row'} spacing={2} sx={{ display: 'flex', height: 60*factor, justifyContent: 'flex-start', padding: '0px 20px', alignItems: 'center' }}>
            <IconButton color='primary'
                sx = {{
                    width: '100%',
                    borderRadius: 2,
                    padding: 0.5,
                    justifyContent: 'flex-start',
                    border: '1px solid transparent',
                    '&:hover': {border: `1px solid ${appTheme.palette.primary.extraLight}`},
                    '&:active': {backgroundColor: 'transparent'},
                    '&:focus': {backgroundColor: 'transparent'},
                }}
            onClick={() => {
                setOpen(true);
            }}>
                <Typography variant='subtitle2' color='primary.main' sx={{ fontWeight: 500, fontSize: 14, display: 'flex', alignItems: 'center', textAlign: 'left', justifyItems: 'center' }}>
                    <LaunchIcon sx={{ fontSize: 16 }} />Signup Form
                </Typography>
            </IconButton>
            <Popover
                open={open}
                anchorEl={document.getElementById('tournament-builder-footer')}
                onClose={() => setOpen(false)}
                anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                sx={{
                    backgroundColor: 'rgba(0, 0, 0, 0.3)',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}  
                PaperProps={{
                    sx: {
                        backgroundColor: 'white',
                        margin: 'auto 3%', // Center horizontally
                        width: '90vw', // Ensure it has a defined width,
                        height: '90vh',
                        padding: 2,
                    }
                }}
            >
                <Stack direction='column' spacing={2} sx={{
                    width: '100%', 
                    height: '100%', 
                    /* padding: 2, */
                    zIndex: 1000,
                    
                }}>
                    <Stack direction={'row'} spacing={2} 
                        sx={{ display: 'flex', height: 60*factor, justifyContent: 'flex-end', 
                            padding: '0px 20px', alignItems: 'center',
                            borderBottom: `1px dotted ${appTheme.palette.primary.light}`,
                        }}
                    >
                        <Typography flex={2} sx={{ fontSize: 16*factor*1.25 }}>Registration Signup Form</Typography>
                        <IconButton color='error'
                            onClick={() => {
                                saveEvent();
                                setOpen(false)
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Stack>
                    <Stack direction="row" spacing={4} sx={{ flex: 1, overflow: 'hidden' }}>

                        <Stack direction='column' spacing={2} sx={{ flex: 1, maxWidth: 240, alignItems: 'center' }}>
                            <Typography variant='subtitle1'>Event Card Thumbnail</Typography>

                            {/* Image preview */}
                            <Box
                                sx={{
                                    width: '100%',
                                    maxWidth: 200,
                                    height: 150,
                                    border: `1px solid ${appTheme.palette.primary.light}`,
                                    borderRadius: 1,
                                    backgroundColor: 'background.paper',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    overflow: 'hidden',
                                }}
                            >
                                {event.eventCardImageURL ? (
                                    <Box
                                        component="img"
                                        src={event.eventCardImageURL}
                                        alt="Event card thumbnail"
                                        sx={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'contain',
                                            display: 'block',
                                        }}
                                    />
                                ) : (
                                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', px: 1 }}>
                                        No image selected
                                    </Typography>
                                )}
                            </Box>

                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<CollectionsOutlinedIcon fontSize="small" />}
                                onClick={() => setImageLibraryOpen(true)}
                                sx={{ width: '100%', maxWidth: 200 }}
                            >
                                Choose from Library
                            </Button>
                        </Stack>
                        {/* Available Fields List */}
                        <Stack 
                            spacing={1} 
                            sx={{
                                width: '300px',
                                padding: 2,
                                border: `1px solid ${appTheme.palette.primary.light}`,
                                borderRadius: 1,
                                backgroundColor: 'background.paper',
                                overflow: 'auto'
                            }}
                            onDragOver={(e) => {
                                e.preventDefault();
                            }}
                            onDrop={(e) => {
                                e.preventDefault();
                                const droppedField = JSON.parse(e.dataTransfer.getData('text/plain'));
                                setSelectedFields(selectedFields.filter((field: any) => field.name !== droppedField.name));
                            }}
                        >
                            <Typography variant="subtitle1" sx={{ borderBottom: `1px solid ${appTheme.palette.primary.light}`, pb: 1 }}>
                                Available Fields
                            </Typography>
                            {availableFields
                                .sort((a, b) => a.name.localeCompare(b.name))
                                .map((field, index) => (
                                    <Box
                                        key={index}
                                        draggable
                                        onDragStart={(e: any) => {
                                            e.dataTransfer.setData('text/plain', JSON.stringify(field));
                                        }}
                                        sx={{
                                            padding: 1,
                                            border: `1px solid ${appTheme.palette.primary.light}`,
                                            borderRadius: 1,
                                            cursor: 'grab',
                                            '&:hover': {
                                                backgroundColor: 'action.hover'
                                            }
                                        }}
                                    >
                                        <Typography>{field.label}</Typography>
                                    </Box>
                                ))}
                        </Stack>

                        {/* Selected Fields List */}
                        <Stack 
                            spacing={1}
                            sx={{
                                flex: 1,
                                padding: 2, 
                                border: `1px solid ${appTheme.palette.primary.light}`,
                                borderRadius: 1,
                                backgroundColor: 'background.paper',
                                overflow: 'auto'
                            }}
                            onDragOver={(e) => {
                                e.preventDefault();
                                // Add a visual indicator for the drop position
                                const dropTarget = e.target as HTMLElement;
                                const draggableBox = dropTarget.closest('.draggable-field');
                                if (draggableBox) {
                                    const rect = draggableBox.getBoundingClientRect();
                                    const isBelow = e.clientY > rect.top + rect.height / 2;
                                    draggableBox.classList.remove('drop-above', 'drop-below');
                                    draggableBox.classList.add(isBelow ? 'drop-below' : 'drop-above');
                                }
                            }}
                            onDragLeave={(e) => {
                                const dropTarget = e.target as HTMLElement;
                                const draggableBox = dropTarget.closest('.draggable-field');
                                if (draggableBox) {
                                    draggableBox.classList.remove('drop-above', 'drop-below');
                                }
                            }}
                            onDrop={(e) => {
                                e.preventDefault();
                                const droppedField = JSON.parse(e.dataTransfer.getData('text/plain'));
                                
                                // Find the drop target and position
                                const dropTarget = e.target as HTMLElement;
                                const draggableBox = dropTarget.closest('.draggable-field');
                                
                                if (draggableBox) {
                                    const dropIndex = parseInt(draggableBox.getAttribute('data-index') || '0');
                                    const rect = draggableBox.getBoundingClientRect();
                                    const isBelow = e.clientY > rect.top + rect.height / 2;
                                    const newIndex = isBelow ? dropIndex + 1 : dropIndex;

                                    // Remove drop indicators
                                    draggableBox.classList.remove('drop-above', 'drop-below');

                                    // Handle reordering within selected fields
                                    const existingIndex = selectedFields.findIndex(
                                        field => field.name === droppedField.name
                                    );

                                    const newSelectedFields = [...selectedFields];
                                    
                                    if (existingIndex >= 0) {
                                        // Reordering existing field
                                        newSelectedFields.splice(existingIndex, 1);
                                        newSelectedFields.splice(newIndex > existingIndex ? newIndex - 1 : newIndex, 0, droppedField);
                                    } else {
                                        // Adding new field from available fields
                                        newSelectedFields.splice(newIndex, 0, droppedField);
                                    }
                                    
                                    setSelectedFields(newSelectedFields);
                                } else {
                                    // Dropped outside of any field, append to end if not already in selected fields
                                    const existingField = selectedFields.find(field => field.name === droppedField.name);
                                    if (!existingField) {
                                        setSelectedFields([...selectedFields, droppedField]);
                                    }
                                }
                            }}
                        >
                            <Typography variant="subtitle1" sx={{ borderBottom: `1px solid ${appTheme.palette.primary.light}`, pb: 1 }}>
                                Form Fields
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 4 }}>
                                Drag fields here to add them to the form
                            </Typography>
                            {selectedFields.map((field, index) => (
                                <Box
                                    key={index}
                                    className="draggable-field"
                                    data-index={index}
                                    draggable
                                    onDragStart={(e) => {
                                        e.dataTransfer.setData('text/plain', JSON.stringify(field));
                                    }}
                                    sx={{
                                        padding: '4px 8px',
                                        border: `1px solid ${appTheme.palette.primary.light}`,
                                        borderRadius: 1,
                                        cursor: 'grab',
                                        position: 'relative',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 0.5,
                                        '&:hover': {
                                            backgroundColor: 'action.hover'
                                        },
                                        '&.drop-above': {
                                            borderTop: `2px solid ${appTheme.palette.primary.main}`
                                        },
                                        '&.drop-below': {
                                            borderBottom: `2px solid ${appTheme.palette.primary.main}`
                                        }
                                    }}
                                >
                                    {/* Header row: drag handle, label, required toggle, delete */}
                                    <Stack direction="row" alignItems="center" spacing={0.5}>
                                        <DragIndicatorIcon 
                                            sx={{ 
                                                color: 'text.secondary',
                                                cursor: 'grab',
                                                fontSize: 20,
                                                flexShrink: 0,
                                            }} 
                                        />
                                        <Typography sx={{ flex: 1 }}>{field.label}</Typography>
                                        <Stack direction={'row'} spacing={0.5} alignItems={'center'}>
                                            <Typography variant='subtitle2'>Team Level</Typography>
                                            <Checkbox 
                                                checked={!!field.regLevelData}
                                                onChange={(e) => {
                                                    setSelectedFields(selectedFields.map(f => f.name === field.name ? {...f, regLevelData: e.target.checked} : f));
                                                }}
                                                size="small"
                                            />
                                        </Stack>
                                        <Stack direction={'row'} spacing={0.5} alignItems={'center'}>
                                            <Typography variant='subtitle2'>Required</Typography>
                                            <Checkbox 
                                                checked={field.required}
                                                onChange={(e) => {
                                                    setSelectedFields(selectedFields.map(f => f.name === field.name ? {...f, required: e.target.checked} : f));
                                                }}
                                                size="small"
                                            />
                                        </Stack>
                                        <IconButton
                                            size="small"
                                            onClick={() => {
                                                setSelectedFields(selectedFields.filter((f) => f.name !== field.name));
                                            }}
                                            sx={{
                                                padding: 0.5,
                                                flexShrink: 0,
                                                '&:hover': {
                                                    color: 'error.main',
                                                }
                                            }}
                                        >
                                            <ClearIcon sx={{ fontSize: 18 }} />
                                        </IconButton>
                                    </Stack>

                                    {/* File URL row (only for checkbox/waiver fields) */}
                                    {field.hasOwnProperty('fileURL') && (
                                        <Stack direction={'row'} spacing={1} alignItems={'center'}>
                                            <Typography variant='subtitle2' sx={{ whiteSpace: 'nowrap' }}>File URL:</Typography>
                                            <Box
                                                component="input"
                                                type="text"
                                                value={field.fileURL}
                                                onChange={(e) => {
                                                    setSelectedFields(selectedFields.map(f => 
                                                        f.name === field.name ? {...f, fileURL: (e.target as HTMLInputElement).value} : f
                                                    ));
                                                }}
                                                sx={{
                                                    flex: 1,
                                                    padding: '4px 8px',
                                                    border: `1px solid ${appTheme.palette.primary.light}`,
                                                    borderRadius: 1,
                                                    '&:focus': {
                                                        outline: 'none',
                                                        borderColor: appTheme.palette.primary.main
                                                    }
                                                }}
                                                placeholder="Enter URL or drop PDF file"
                                                onDrop={async (e) => {
                                                    e.preventDefault();
                                                    const file = e.dataTransfer.files[0];
                                                    if (file) {
                                                        const formData = new FormData();
                                                        formData.append('file', file);
                                                        formData.append('_id', event._id);
                                                        formData.append('userID', auth?.user?.profile?.sub || '');

                                                        try {
                                                            const response = await api.post('/image/uploadEventFile', formData, {
                                                                headers: {
                                                                    'Content-Type': 'multipart/form-data',
                                                                },
                                                            });

                                                            if (response.status === 200) {
                                                                const data = response.data;
                                                                setSelectedFields(selectedFields.map(f => 
                                                                    f.name === field.name ? {...f, fileURL: data.fileUrl} : f
                                                                ));
                                                            } else {
                                                                console.error('Failed to upload file');
                                                            }
                                                        } catch (error) {
                                                            console.error('Error uploading file:', error);
                                                        }
                                                    }
                                                }}
                                                onDragOver={(e) => e.preventDefault()}
                                            />
                                        </Stack>
                                    )}

                                    {/* Info tooltip row (all fields) */}
                                    <Stack direction={'column'} spacing={0.25}>
                                        <Typography variant='subtitle2'>Info Tooltip:</Typography>
                                        <Box
                                            component="textarea"
                                            value={field.tooltip ?? ''}
                                            rows={1}
                                            onChange={(e) => {
                                                setSelectedFields(selectedFields.map(f =>
                                                    f.name === field.name ? { ...f, tooltip: (e.target as HTMLTextAreaElement).value } : f
                                                ));
                                            }}
                                            placeholder="Optional disclaimer or info text shown to registrants"
                                            sx={{
                                                width: '100%',
                                                padding: '4px 8px',
                                                border: `1px solid ${appTheme.palette.primary.light}`,
                                                borderRadius: 1,
                                                resize: 'vertical',
                                                fontFamily: 'inherit',
                                                fontSize: '0.8125rem',
                                                lineHeight: 1.5,
                                                boxSizing: 'border-box',
                                                '&:focus': {
                                                    outline: 'none',
                                                    borderColor: appTheme.palette.primary.main,
                                                },
                                            }}
                                        />
                                    </Stack>
                                </Box>
                            ))}
                        </Stack>
                    </Stack>
                    
                </Stack>
            </Popover>

            <ImageLibrary
                open={imageLibraryOpen}
                onClose={() => setImageLibraryOpen(false)}
                mode="select"
                onSelect={async ({ url }) => {
                    try {
                        await api.post('/tb/saveImage', {
                            _id: event._id,
                            userID: auth?.user?.profile?.sub,
                            eventCardImageURL: url,
                        });
                        setViewEvent((prev) =>
                            prev ? { ...prev, eventCardImageURL: url } : null
                        );
                    } catch (error) {
                        console.error('Error saving event card image:', error);
                    }
                }}
            />
        </Stack>
    )
}
